import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Menu,
    MenuItem,
    Typography,
    Paper,
    Toolbar,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Header from '../../components/Header';

export default function AgentsPage() {
    const [agents, setAgents] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuAgentId, setMenuAgentId] = useState(null);
    const [openConfig, setOpenConfig] = useState(false);
    const [current, setCurrent] = useState(null);
    const [testResult, setTestResult] = useState(null);
    const [openTestResult, setOpenTestResult] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        axios.get('/api/vapi/assistants')
            .then(res => {
                setAgents(res.data);
            })
            .catch(err => console.error('Failed to load agents:', err));
    }, []);

    const openMenu = (e, id) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setMenuAgentId(id);
    };

    const closeMenu = () => {
        setAnchorEl(null);
        setMenuAgentId(null);
    };

    const handleConfigure = () => {
        const agent = agents.find(a => a.id === menuAgentId);
        setCurrent({
            ...agent,
            emailEnabled: agent.emailEnabled ?? false,
            crmConfig: agent.crmConfig || { endpoint: '' },
            calendarSync: agent.calendarSync || 'none',
            testSuiteId: agent.testSuiteId || ''
        });
        setOpenConfig(true);
        closeMenu();
    };

    const handleRunTest = async () => {
        const agent = agents.find(a => a.id === menuAgentId);
        closeMenu();

        if (!agent.testSuiteId) {
            alert('This agent has no testSuiteId configured.');
            return;
        }

        try {
            const res = await axios.post(`/api/vapi/assistants/${agent.id}/run-test`, {
                testSuiteId: agent.testSuiteId,
                name: `${agent.name} Test Run`
            });
            setTestResult(res.data);
            setOpenTestResult(true);
        } catch (err) {
            console.error('Failed to run test:', err.response?.data || err.message);
            alert('Failed to run test');
        }
    };

    const saveConfig = () => {
        axios.patch(`/api/vapi/assistants/${current.id}`, {
            emailEnabled: current.emailEnabled,
            crmConfig: current.crmConfig,
            calendarSync: current.calendarSync,
            testSuiteId: current.testSuiteId
        })
            .then(res => {
                setAgents(list =>
                    list.map(a => (a.id === res.data.id ? res.data : a))
                );
                setOpenConfig(false);
            })
            .catch(err => console.error('Failed to save settings:', err));
    };

    const handleRowClick = id => {
        navigate(`/agents/${id}`);
    };

    return (
        <Box m="20px">
            <Header title="Agents" subtitle="Manage your AI agents" />

            <Paper>
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Agents
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/agents/new')}
                    >
                        Add Agent
                    </Button>
                </Toolbar>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agents.map(agent => (
                            <TableRow
                                key={agent.id}
                                hover
                                onClick={() => handleRowClick(agent.id)}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{agent.name}</TableCell>
                                <TableCell>{agent.description}</TableCell>
                                <TableCell align="right" onClick={e => e.stopPropagation()}>
                                    <IconButton onClick={e => openMenu(e, agent.id)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                <MenuItem onClick={handleConfigure}>Configure</MenuItem>
                <MenuItem onClick={handleRunTest}>Run Test</MenuItem>
            </Menu>

            <Dialog open={openConfig} onClose={() => setOpenConfig(false)} fullWidth maxWidth="sm">
                <DialogTitle>Configure {current?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id="email-enabled-label">Email Invites</InputLabel>
                        <Select
                            labelId="email-enabled-label"
                            label="Email Invites"
                            value={current?.emailEnabled ? 'enabled' : 'disabled'}
                            onChange={e => {
                                const val = e.target.value === 'enabled';
                                setCurrent(c => ({ ...c, emailEnabled: val }));
                            }}
                        >
                            <MenuItem value="enabled">Enabled</MenuItem>
                            <MenuItem value="disabled">Disabled</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="CRM Endpoint"
                        fullWidth
                        value={current?.crmConfig?.endpoint || ''}
                        onChange={e => {
                            const cfg = { ...current.crmConfig, endpoint: e.target.value };
                            setCurrent(c => ({ ...c, crmConfig: cfg }));
                        }}
                    />

                    <FormControl fullWidth>
                        <InputLabel id="calendar-sync-label">Calendar Sync</InputLabel>
                        <Select
                            labelId="calendar-sync-label"
                            label="Calendar Sync"
                            value={current?.calendarSync || 'none'}
                            onChange={e =>
                                setCurrent(c => ({ ...c, calendarSync: e.target.value }))
                            }
                        >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="google">Google Calendar</MenuItem>
                            <MenuItem value="outlook">Outlook Calendar</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Test Suite ID"
                        fullWidth
                        value={current?.testSuiteId || ''}
                        onChange={e =>
                            setCurrent(c => ({ ...c, testSuiteId: e.target.value }))
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfig(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveConfig}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openTestResult} onClose={() => setOpenTestResult(false)} fullWidth maxWidth="md">
                <DialogTitle>Test Results for {testResult?.name}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography>Status: <b>{testResult?.status}</b></Typography>

                    {testResult?.testResults?.map((result, i) => (
                        <Box key={i} sx={{ p: 2, border: '1px solid #444', borderRadius: 2 }}>
                            <Typography variant="h6">{result.test.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {result.test.script}
                            </Typography>

                            {result.attempts.map((attempt, j) => (
                                <Box key={j} mt={2}>
                                    <Typography variant="subtitle2">Attempt {j + 1}</Typography>
                                    {attempt.scorerResults.map((score, k) => (
                                        <Box key={k} mt={1}>
                                            <Typography color={score.result === 'pass' ? 'green' : 'red'}>
                                                Result: {score.result.toUpperCase()}
                                            </Typography>
                                            <Typography>Reasoning: {score.reasoning}</Typography>
                                        </Box>
                                    ))}

                                    {attempt.call?.artifact?.recordingUrl && (
                                        <Box mt={2}>
                                            <audio controls src={attempt.call.artifact.recordingUrl} />
                                            <Typography variant="caption">
                                                Transcript: {attempt.call.artifact.transcript}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenTestResult(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
